import React from 'react';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import EmployeeProfileFormFields from '../../../components/profile/EmployeeProfileFormFields';
import { shortenWalletAddress } from '../../../utils/commonFunction';

const EditEmployeePage = ({
    employee,
    form,
    onChange,
    onSubmit,
    onCancel,
    onRemove,
    submitting,
    isCompanyOwner,
    backendToken,
    inputClass,
}) => {
    const isRemoved =
        String(employee?.status || '').toLowerCase() === 'removed';
    const wallet = employee?.wallet || '';

    return (
        <div className="mx-auto w-full max-w-3xl space-y-6 pb-10">
            <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 transition hover:text-teal-700 disabled:opacity-50 dark:text-zinc-400 dark:hover:text-teal-400"
            >
                <i className="fa-solid fa-arrow-left text-[10px]" aria-hidden />
                Back
            </button>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-start gap-4">
                    {wallet ? (
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-zinc-100 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-700">
                            <Jazzicon
                                diameter={56}
                                seed={jsNumberForAddress(wallet)}
                            />
                        </div>
                    ) : null}
                    <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                            Team member
                        </p>
                        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">
                            Edit employee
                        </h1>
                        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                            {employee?.name || 'Unnamed employee'}
                            {wallet ? (
                                <span className="font-mono text-zinc-500 dark:text-zinc-500">
                                    {' '}
                                    · {shortenWalletAddress(wallet)}
                                </span>
                            ) : null}
                        </p>
                    </div>
                </div>
            </div>

            {isRemoved ? (
                <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50">
                    <p className="text-sm text-zinc-600 dark:text-zinc-300">
                        This employee has been removed from your team and can no longer be
                        edited.
                    </p>
                    <div className="mt-4 flex justify-end">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="rounded-xl border border-zinc-300 bg-zinc-100 px-4 py-2 text-sm font-semibold text-zinc-800 transition-colors hover:border-zinc-400 hover:bg-zinc-200 dark:border-zinc-600 dark:bg-zinc-800/70 dark:text-zinc-200"
                        >
                            Back to employees
                        </button>
                    </div>
                </div>
            ) : (
                <div className="rounded-2xl border border-teal-200/80 bg-white p-6 shadow-sm md:p-8 dark:border-teal-900/40 dark:bg-zinc-950/50">
                    <div className="mb-6 flex items-start gap-3 border-b border-zinc-200 pb-4 dark:border-zinc-800">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-xs font-bold text-teal-800 ring-1 ring-teal-200 dark:bg-teal-900/50 dark:text-teal-200 dark:ring-teal-800">
                            <i className="fa-solid fa-pen text-[10px]" aria-hidden />
                        </span>
                        <div>
                            <h2 className="text-base font-semibold text-zinc-900 dark:text-white">
                                Profile & payroll
                            </h2>
                            <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">
                                Update contact details, compensation, and status. Wallet
                                address cannot be changed.
                            </p>
                        </div>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-6">
                        <EmployeeProfileFormFields
                            form={form}
                            onChange={onChange}
                            inputClass={inputClass}
                            backendToken={backendToken}
                            walletAddress={wallet}
                            disabled={submitting || !isCompanyOwner}
                            showStatus
                        />

                        <div className="space-y-2">
                            <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                                Wallet address (read-only)
                            </label>
                            <input
                                type="text"
                                readOnly
                                value={wallet}
                                className={`${inputClass} cursor-not-allowed font-mono text-sm opacity-80`}
                            />
                        </div>

                        {!isCompanyOwner ? (
                            <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                                Connect with the company owner wallet to edit employees.
                            </p>
                        ) : null}

                        <div className="flex flex-col-reverse gap-3 border-t border-zinc-200 pt-6 sm:flex-row sm:items-center sm:justify-between dark:border-zinc-800">
                            {isCompanyOwner ? (
                                <button
                                    type="button"
                                    onClick={onRemove}
                                    disabled={submitting}
                                    className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-800 transition-colors hover:border-rose-300 hover:bg-rose-100 disabled:opacity-50 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20"
                                >
                                    Remove from team
                                </button>
                            ) : (
                                <span />
                            )}
                            <div className="flex flex-wrap justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={onCancel}
                                    disabled={submitting}
                                    className="rounded-xl border border-zinc-300 bg-zinc-100 px-4 py-2.5 text-sm font-semibold text-zinc-800 transition-colors hover:border-zinc-400 hover:bg-zinc-200 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800/70 dark:text-zinc-200 dark:hover:border-zinc-500 dark:hover:bg-zinc-700/80"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!isCompanyOwner || submitting}
                                    className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-teal-600 dark:hover:bg-teal-500"
                                >
                                    {submitting ? (
                                        <>
                                            <i
                                                className="fa-solid fa-spinner fa-spin text-xs"
                                                aria-hidden
                                            />
                                            Saving…
                                        </>
                                    ) : (
                                        <>
                                            <i
                                                className="fa-solid fa-check text-xs"
                                                aria-hidden
                                            />
                                            Save changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default EditEmployeePage;
